/**
 * `useTaskChangeDigest` — the data layer for B3.6 "what changed since you last
 * looked" (issue #765). One query over the already-merged digest endpoint:
 *
 *     GET /api/teams/{teamId}/tasks/changes?since=<ISO>
 *       → ChangeDigest { since, new_tasks[], state_changes[], mentions[] }
 *
 * BRAND-CRITICAL (§A9 — the digest is THE surveillance-risk vector). The
 * endpoint is ALREADY scoped server-side to the requesting member's OWN slice:
 *   - new tasks created on the member's own branch since `since`
 *   - state changes on the member's own nodes since `since`
 *   - notes that `@`-mention the member since `since`
 * It is NEVER a feed of what other people did, never a count/tally about a
 * person. The frontend renders exactly what the endpoint returns and adds no
 * aggregation of its own.
 *
 * `since` is resolved client-side from the member's stored "last looked"
 * stamp (`lastLooked.ts`) — a per-member, per-device cue, on the member's
 * terms. The hook takes the resolved `since` so the caller controls when to
 * advance the stamp (i.e. only when the panel is actually shown, so the member
 * doesn't silently "miss" changes).
 *
 * Clean query/view split (§A7): the hook owns fetching + caching; the panel
 * consumes the digest fields and renders them. `retry: false` + an exposed
 * `isError` lets the panel degrade calmly (the endpoint may not be deployed).
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema types.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type ChangeDigest = components["schemas"]["ChangeDigest"];
export type DigestNewTask = components["schemas"]["DigestNewTask"];
export type DigestStateChange = components["schemas"]["DigestStateChange"];
/** One mention row — a flat `{ note_id, node_id, node_title }` dict (the seam shape). */
export type DigestMention = Record<string, string>;

/** Cache key for a team's digest at a `since` instant. Exported for tests. */
export const CHANGE_DIGEST_KEY = (teamId: string, since: string) =>
  ["task-change-digest", teamId, since] as const;

// A digest is a one-off orientation read on opening the map; we don't want it
// re-running mid-session, so a long stale time keeps the first answer stable.
const STALE_TIME_MS = 5 * 60_000;

export interface UseTaskChangeDigestResult {
  /** The member's own slice since `since`, or null while loading / on error. */
  readonly digest: ChangeDigest | null;
  /**
   * True when there is at least one item across all three slices. A digest
   * with nothing in it is shown as a calm "nothing new" — but the caller may
   * choose not to surface the panel at all when empty.
   */
  readonly hasContent: boolean;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

function countItems(d: ChangeDigest | null | undefined): number {
  if (!d) return 0;
  const news = Array.isArray(d.new_tasks) ? d.new_tasks.length : 0;
  const states = Array.isArray(d.state_changes) ? d.state_changes.length : 0;
  const mentions = Array.isArray(d.mentions) ? d.mentions.length : 0;
  return news + states + mentions;
}

/**
 * Fetch the member's own change digest since `since`. `enabled` only when a
 * team AND a `since` are present (i.e. the digest panel is being shown). Each
 * distinct `since` is its own cache entry.
 */
export function useTaskChangeDigest(
  teamId: string | undefined,
  since: string | undefined,
): UseTaskChangeDigestResult {
  const query = useQuery({
    queryKey: CHANGE_DIGEST_KEY(teamId ?? "", since ?? ""),
    enabled: Boolean(teamId) && Boolean(since),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () =>
      apiFetch<ChangeDigest>(
        `/api/teams/${teamId}/tasks/changes?since=${encodeURIComponent(since as string)}`,
      ),
  });

  const digest = query.data ?? null;
  return {
    digest,
    hasContent: countItems(digest) > 0,
    isLoading: query.isLoading && Boolean(teamId) && Boolean(since),
    isError: query.isError,
  };
}
